import { z } from "zod";
/* The clinic feed's domain timeline: one entry per fact that happened to a
 * client, read at request time as a union over domain tables (see
 * apps/api/src/modules/patients/timeline.ts).
 *
 * Entries carry **structured data, not rendered copy**. The first version of
 * this contract sent `title`/`detail` as English strings built on the server,
 * which put user-visible copy outside @ferrivia/i18n — the one place every
 * other clinic string goes through — and left the reader's locale unable to
 * format an order total, a draw date or a declared choice. Each kind below
 * therefore carries its own fields and the console renders the words.
 *
 * Nothing here derives, scores, thresholds, flags, colour-codes or ranks a
 * measured value: a lab entry states provenance, dates, source and how many
 * measurements exist, and carries no value at all (root CLAUDE.md, "The
 * software does not interpret"; REQ-MDR-002). Reading the numbers is the Labs
 * tab's job, which is also where the asiakastietolaki 10 § disclosure for
 * reading them is logged (`patient_results`, migration 020) — the timeline
 * logs `patient_timeline` and must stay worth exactly that.
 *
 * Kinds are read leniently. `timelineFeedSchema` drops an entry whose kind
 * this build does not know rather than failing the whole feed, so a clinic
 * bundle held in a stale cache renders the strands it understands instead of
 * erroring — the same lenient-read stance as `readHomeBlockSchema` in home.ts.
 * Enum-shaped fields (`provenance`, `channel`, `collectionMethod`, …) are
 * plain strings here for the same reason: a value added by a newer server
 * renders as its raw string rather than throwing.
 */
export const timelineEntryKindSchema = z.enum([
    "signup",
    "lab_purchase",
    "specimen_collected",
    "lab_result",
    "intent_declared",
]);
const entryBase = {
    id: z.string(),
    occurredAt: z.string(),
};
/** Account created. `provisionedVia` is `users.provisioned_via`: a webstore
    buyer's account is created *by* the checkout ('purchase') and has no
    `identities` row, so `authMethod` is null and the console must not claim a
    sign-in method it does not have. */
export const signupTimelineEntrySchema = z.object({
    ...entryBase,
    kind: z.literal("signup"),
    provisionedVia: z.string(),
    authMethod: z.string().nullable(),
});
/** Where a purchase came from, as far as the order itself can say.
    `imported` is the honest answer for a legacy row whose product name does
    not identify the channel — not a guess at one. */
export const purchaseChannelSchema = z.enum(["webstore", "event", "rautapaiva", "imported"]);
/** An order carrying a lab panel reached `paid`. Occurs at `paid_at`, never
    `placed_at` — an abandoned checkout is not an event in a client's life. */
export const labPurchaseTimelineEntrySchema = z.object({
    ...entryBase,
    kind: z.literal("lab_purchase"),
    orderNumber: z.string(),
    channel: z.string(),
    items: z.array(z.object({ productName: z.string(), quantity: z.number() })),
    totalCents: z.number(),
    currency: z.string(),
});
/** A sample was collected. Rautapäivä draws only, on purpose: at a booth we
    record the specimen ourselves, so the fact is ours and it is timely. For a
    webstore draw no signal reaches us when the client visits the lab, so a
    specimen row appears only alongside the result — an entry built from it
    would claim we knew something at a time we did not, and its absence would
    read as "hasn't been drawn yet", which is not knowable. Revisit when the
    lab integration lands. */
export const specimenTimelineEntrySchema = z.object({
    ...entryBase,
    kind: z.literal("specimen_collected"),
    collectedOn: z.string(),
    collectionMethod: z.string(),
    collectingOrg: z.string().nullable(),
});
/** A result exists. One kind for both provenances: what differs to a
    clinician is who produced it, which is content (`provenance`, `labCode`,
    `sourceLab`), not a different sort of event. Occurs at `reported_at` —
    when it reached us — and states `collectedOn` as the draw date, because a
    client can upload a two-year-old paper result and the feed has to place it
    at the upload. `analyteCount` is a size, never a value. */
export const labResultTimelineEntrySchema = z.object({
    ...entryBase,
    kind: z.literal("lab_result"),
    provenance: z.string(),
    labCode: z.string().nullable(),
    sourceLab: z.string().nullable(),
    collectedOn: z.string(),
    analyteCount: z.number(),
});
/** A client-declared crossroads — `day0_intent` today. Every declaration is
    an entry, not just the latest: `journey_declarations` is append-only with a
    re-prompt path (019_journey_declarations.sql), and a client changing their
    mind is exactly the moment the conversation should change, so `supersedes`
    names the answer this one replaced. The declaration is displayed as
    declared and is never connected to anything measured. */
export const intentTimelineEntrySchema = z.object({
    ...entryBase,
    kind: z.literal("intent_declared"),
    declarationKind: z.string(),
    choice: z.string(),
    supersedes: z
        .object({
        choice: z.string(),
        declaredAt: z.string(),
    })
        .nullable(),
});
export const timelineEntrySchema = z.discriminatedUnion("kind", [
    signupTimelineEntrySchema,
    labPurchaseTimelineEntrySchema,
    specimenTimelineEntrySchema,
    labResultTimelineEntrySchema,
    intentTimelineEntrySchema,
]);
/** The feed as read by a client of this API: newest first, with entries of an
    unknown kind dropped rather than failing the array (see the lenient-read
    note at the top of this file). */
export const timelineFeedSchema = z.array(z.unknown()).transform((rows) => rows.flatMap((row) => {
    const parsed = timelineEntrySchema.safeParse(row);
    return parsed.success ? [parsed.data] : [];
}));
